import { useState, useCallback } from 'react';
import { Button, Snackbar, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { EquipmentPickerDialog } from '@components/EquipmentPicker';
import { useAddWorkOrderEquipment } from '@api/bff/workOrders/useAddWorkOrderEquipment';
import type { EquipmentSummary } from '@api/bff/equipment/useEquipment';

type SnackState = {
  severity: 'success' | 'error';
  message: string;
};

type Props = {
  jobNumber: string;
  defaultCompanyName?: string | null;
  disabled?: boolean;
};

export const AddEquipmentButton = ({
  jobNumber,
  defaultCompanyName = null,
  disabled = false
}: Props) => {
  const [open, setOpen] = useState(false);
  const [snack, setSnack] = useState<SnackState | null>(null);
  const addEquipment = useAddWorkOrderEquipment();

  const handleSelect = useCallback(
    (equipment: EquipmentSummary) => {
      addEquipment.mutate(
        { jobNumber, equipment },
        {
          onSuccess: result => {
            const persistedId = (result?.id ?? '').trim();
            if (persistedId === '') {
              setSnack({
                severity: 'error',
                message: `Could not confirm ${equipment.id} was saved. Refresh to verify before continuing.`
              });
              return;
            }
            setSnack({
              severity: 'success',
              message: `Added ${equipment.id} to the work order`
            });
          },
          onError: error =>
            setSnack({
              severity: 'error',
              message: `Failed to add equipment: ${error.message}`
            })
        }
      );
    },
    [addEquipment, jobNumber]
  );

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={() => setOpen(true)}
        disabled={disabled || addEquipment.isPending}
      >
        Add Equipment
      </Button>
      <EquipmentPickerDialog
        open={open}
        defaultCompanyName={defaultCompanyName}
        onClose={() => setOpen(false)}
        onSelect={handleSelect}
      />
      {snack && (
        <Snackbar
          open
          autoHideDuration={4000}
          onClose={() => setSnack(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={snack.severity}
            variant="filled"
            onClose={() => setSnack(null)}
          >
            {snack.message}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};
